import { IconButton, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import React, { useState } from 'react'
import { com } from '@eidu/form'
import { useSnackBar } from '../feedback/SnackBarProvider'
import SemanticLinkField from './SemanticLinkField'
import SemanticLinkDisplay from '../data/SemanticLinkDisplay'
import deleteSemanticLink from '../../io/api/analytics/deleteSemanticLink'
import SemanticLinkConfig from '../../types/SemanticLinkConfig'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import SemanticLink = com.eidu.sharedlib.entity.analytics.SemanticLink
import SemanticLinkId = com.eidu.sharedlib.entity.analytics.SemanticLinkId

type SemanticLinksItemProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  semanticLinks: readonly SemanticLink[]
  semanticLinkConfig: SemanticLinkConfig
  onSemanticLinksChange: () => void
}

const SemanticLinksItem = ({
  entityType,
  entityTypes,
  semanticLinks,
  semanticLinkConfig,
  onSemanticLinksChange,
}: SemanticLinksItemProps) => {
  const [creating, setCreating] = useState(false)

  const { showSnackBar } = useSnackBar()

  const linksForKey = semanticLinks.filter(({ key }) => key.equals(semanticLinkConfig.key))
  const canAddLink = linksForKey.length === 0 || semanticLinkConfig.duplicatesAllowed

  const handleSave = async () => {
    setCreating(false)
    onSemanticLinksChange()
  }

  const handleDelete = async (semanticLinkId: SemanticLinkId) => {
    try {
      await deleteSemanticLink({ semanticLinkId }).then(() => {
        onSemanticLinksChange()
        showSnackBar('Successfully deleted semantic link', 'success')
      })
    } catch (error) {
      showSnackBar('Error deleting semantic link', 'error')
      console.error(error)
    }
  }

  return (
    <Stack key={semanticLinkConfig.key.toString()} spacing={1}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Typography>{semanticLinkConfig.title}</Typography>
          {canAddLink && !creating && (
            <IconButton color="primary" onClick={() => setCreating(true)} size="small">
              <AddIcon />
            </IconButton>
          )}
        </Stack>

        {creating && (
          <SemanticLinkField
            entityType={entityType.id}
            entityTypes={entityTypes}
            semanticLinkConfig={semanticLinkConfig}
            onSave={handleSave}
            onCancel={() => setCreating(false)}
          />
        )}

        {!(linksForKey.length === 0 && creating) && (
          <SemanticLinkDisplay
            entityTypeId={entityType.id}
            entityTypes={entityTypes}
            fieldType={semanticLinkConfig.fieldType}
            semanticLinks={linksForKey}
            onDelete={handleDelete}
          />
        )}
      </Stack>
    </Stack>
  )
}

export default SemanticLinksItem
